import { ReactNode, useCallback, useMemo } from "react";
import { AppId, FCX, ID, usePlatformContext } from "@datassential/platform-ui-lib";
import { Grid } from "@components/new/Grid/Grid";
import LTOItem from "@pages/_shared/LTOItem/LTOItem";
import HomeSectionFooter from "@pages/Home/components/HomeSectionFooter";
import AccessLink from "@components/AccessLink/AccessLink";
import { PermissionLevelEnum } from "@models/PermissionSection";
import { toast } from "react-toastify";
import { requestTrialByFeature } from "@actions/page";
import { useDispatch } from "react-redux";
import HomeSectionHeader from "./HomeSectionHeader";
import InputRadio from "@components/new/InputRadio/InputRadio";
import useLocalStorageState from "@hooks/useLocalStorageState";
import { useHomeLtos } from "@apiHooks/useHomeLtos";

interface HomeLtosSectionProps {
    data: ReturnType<typeof useHomeLtos>['data'];
    categoryId: ID | null;
    maxRowsCount?: number;
    topPerformingMonths?: number;
    footerLinkLabel?: string;
    footerLinkPath?: string;
    headerContent: ReactNode;
}

const HomeLtosSection: FCX<HomeLtosSectionProps> = ({
    headerContent,
    data,
    categoryId,
    maxRowsCount = 2,
    topPerformingMonths = 6,
    footerLinkLabel,
    footerLinkPath = '',
}) => {
    const dispatch = useDispatch();
    const { userInfo: { products } } = usePlatformContext();

    const [launchesType, setLaunchesType] = useLocalStorageState<'latest' | 'top'>(
        'home-ltos-is-top-launches',
        'latest',
    );

    const launchesRatings = useMemo(
        () => products[AppId.LaunchesAndRatings],
        [products],
    );

    const items = useMemo(
        () => {
            if (categoryId === null) return [];

            const itemsMap = launchesType === 'top'
                ? data?.itemsData.data.topItems
                : data?.itemsData.data.recentItems;

            return itemsMap?.get(categoryId) ?? [];
        },
        [data, launchesType, categoryId],
    );

    const hasAccess = useMemo(
        () => data?.itemsData.permissionLevel === PermissionLevelEnum.Full,
        [data]
    );

    const requestTrial = useCallback(() => {
        toast.info('Request has been sent');
        dispatch(requestTrialByFeature('scores'));
    }, [dispatch]);

    return (
        <div className="HomeSection__content">
            <HomeSectionHeader className="no-pt">
                <div className="HomeSectionHeader__part">
                    {headerContent}
                    <InputRadio
                        label="Most Recent"
                        isActive={launchesType === 'latest'}
                        onChange={() => setLaunchesType('latest')}
                    />
                    <InputRadio
                        label={`Top Performing (past ${topPerformingMonths} months)`}
                        isActive={launchesType === 'top'}
                        onChange={() => setLaunchesType('top')}
                    />
                </div>
            </HomeSectionHeader>
            <Grid maxRowsCount={maxRowsCount}>
                {items.map((item, index) => ({
                    key: `${launchesType}-${item.id}-${index}`,
                    value: (
                        <LTOItem
                            data={item}
                            location="chain page"
                        />
                    ),
                }))}
            </Grid>
            <HomeSectionFooter style={{ marginTop: -20 }}>
                <AccessLink
                    url={launchesRatings.url ? `${launchesRatings.url}${footerLinkPath}` : ''}
                    text={footerLinkLabel || 'More Launches & Ratings'}
                    altText="Upgrade your subscription to reveal new items from top chains."
                    hasAccess={hasAccess}
                    onContactUsClick={requestTrial}
                    testId="linkHomeFoodsLtosMoreLaunches"
                />
            </HomeSectionFooter>
        </div>
    );
};

export default HomeLtosSection;
